import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  api,
  type CreateMachineInput,
  type HardwareOptions,
  type Machine,
  type MachineDetailsInput,
} from "@/lib/api";

const NO_PROFILE = "__none__";
const EMPTY_DETAILS: MachineDetailsInput = {
  name: "",
  serial_number: null,
  box_number: null,
  buyer_name: null,
  sold_at: null,
  board: null,
  memory: null,
  chip: null,
  power_supply: null,
  case: null,
  display: null,
};
const EMPTY_CREATE_DETAILS: CreateMachineInput = { ...EMPTY_DETAILS, certificate_pem: "" };

function formatDate(value: string | null): string {
  if (!value) return "never";
  return new Date(value).toLocaleString();
}

function toDetailsInput(machine: Machine): MachineDetailsInput {
  return {
    name: machine.name,
    serial_number: machine.serial_number,
    box_number: machine.box_number,
    buyer_name: machine.buyer_name,
    sold_at: machine.sold_at ? machine.sold_at.slice(0, 10) : null,
    board: machine.board,
    memory: machine.memory,
    chip: machine.chip,
    power_supply: machine.power_supply,
    case: machine.case,
    display: machine.display,
  };
}

function nullIfEmpty(value: string): string | null {
  return value.trim() === "" ? null : value.trim();
}

const EMPTY_HARDWARE_OPTIONS: HardwareOptions = {
  board: [],
  memory: [],
  chip: [],
  power_supply: [],
  case: [],
  display: [],
};

function MachineDetailsForm({
  details,
  onChange,
  idPrefix,
  hardwareOptions = EMPTY_HARDWARE_OPTIONS,
}: {
  details: MachineDetailsInput;
  onChange: (patch: Partial<MachineDetailsInput>) => void;
  idPrefix: string;
  hardwareOptions?: HardwareOptions;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-name`}>Name</Label>
        <Input
          id={`${idPrefix}-name`}
          value={details.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g. customer-acme-01"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-serial`}>Serial number</Label>
          <Input
            id={`${idPrefix}-serial`}
            value={details.serial_number ?? ""}
            onChange={(e) => onChange({ serial_number: nullIfEmpty(e.target.value) })}
            placeholder="SN-00123"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-box`}>Box number</Label>
          <Input
            id={`${idPrefix}-box`}
            value={details.box_number ?? ""}
            onChange={(e) => onChange({ box_number: nullIfEmpty(e.target.value) })}
            placeholder="42"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-buyer`}>Buyer / company name</Label>
          <Input
            id={`${idPrefix}-buyer`}
            value={details.buyer_name ?? ""}
            onChange={(e) => onChange({ buyer_name: nullIfEmpty(e.target.value) })}
            placeholder="Acme GmbH"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-sold`}>Date sold</Label>
          <Input
            id={`${idPrefix}-sold`}
            type="date"
            value={details.sold_at ?? ""}
            onChange={(e) => onChange({ sold_at: nullIfEmpty(e.target.value) })}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-board`}>Board</Label>
          <Combobox
            id={`${idPrefix}-board`}
            value={details.board ?? ""}
            onChange={(value) => onChange({ board: nullIfEmpty(value) })}
            options={hardwareOptions.board}
            placeholder="Raspberry Pi 5"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-memory`}>Memory</Label>
          <Combobox
            id={`${idPrefix}-memory`}
            value={details.memory ?? ""}
            onChange={(value) => onChange({ memory: nullIfEmpty(value) })}
            options={hardwareOptions.memory}
            placeholder="8GB"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-chip`}>Chip</Label>
          <Combobox
            id={`${idPrefix}-chip`}
            value={details.chip ?? ""}
            onChange={(value) => onChange({ chip: nullIfEmpty(value) })}
            options={hardwareOptions.chip}
            placeholder="BCM2712"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-power-supply`}>Power supply</Label>
          <Combobox
            id={`${idPrefix}-power-supply`}
            value={details.power_supply ?? ""}
            onChange={(value) => onChange({ power_supply: nullIfEmpty(value) })}
            options={hardwareOptions.power_supply}
            placeholder="27W USB-C"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-case`}>Case</Label>
          <Combobox
            id={`${idPrefix}-case`}
            value={details.case ?? ""}
            onChange={(value) => onChange({ case: nullIfEmpty(value) })}
            options={hardwareOptions.case}
            placeholder="Official Pi 5 Case"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-display`}>Display</Label>
          <Combobox
            id={`${idPrefix}-display`}
            value={details.display ?? ""}
            onChange={(value) => onChange({ display: nullIfEmpty(value) })}
            options={hardwareOptions.display}
            placeholder='7" touchscreen'
          />
        </div>
      </div>
    </div>
  );
}

function ProfileSelect({
  machineId,
  profileId,
}: {
  machineId: string;
  profileId: string | null;
}) {
  const queryClient = useQueryClient();
  const { data: profiles } = useQuery({ queryKey: ["profiles"], queryFn: api.listProfiles });

  const setProfile = useMutation({
    mutationFn: (nextProfileId: string | null) => api.setMachineProfile(machineId, nextProfileId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["machines"] }),
  });

  return (
    <Select
      value={profileId ?? NO_PROFILE}
      onValueChange={(value) => setProfile.mutate(value === NO_PROFILE ? null : value)}
    >
      <SelectTrigger size="sm" className="w-36">
        <SelectValue placeholder="No profile" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_PROFILE}>No profile</SelectItem>
        {profiles?.map((profile) => (
          <SelectItem key={profile.id} value={profile.id}>
            {profile.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EditMachineDialog({ machine }: { machine: Machine }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState<MachineDetailsInput>(() => toDetailsInput(machine));
  const { data: hardwareOptions } = useQuery({
    queryKey: ["hardware-options"],
    queryFn: api.getHardwareOptions,
  });

  const updateDetails = useMutation({
    mutationFn: (input: MachineDetailsInput) => api.updateMachineDetails(machine.id, input),
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["machines"] });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDetails(toDetailsInput(machine));
    setOpen(nextOpen);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!details.name.trim()) return;
    updateDetails.mutate(details);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="link" className="h-auto p-0">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit machine</DialogTitle>
            <DialogDescription>Update inventory and sale details.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <MachineDetailsForm
              details={details}
              onChange={(patch) => setDetails((prev) => ({ ...prev, ...patch }))}
              idPrefix="edit"
              hardwareOptions={hardwareOptions}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={updateDetails.isPending}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddMachineDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<"cert" | "details">("cert");
  const [newDetails, setNewDetails] = useState<CreateMachineInput>(EMPTY_CREATE_DETAILS);
  const { data: hardwareOptions } = useQuery({
    queryKey: ["hardware-options"],
    queryFn: api.getHardwareOptions,
  });

  const createMachine = useMutation({
    mutationFn: api.createMachine,
    onSuccess: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["machines"] });
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setStep("cert");
      setNewDetails(EMPTY_CREATE_DETAILS);
    }
  };

  const handleCertContinue = (event: FormEvent) => {
    event.preventDefault();
    if (!newDetails.certificate_pem.trim()) return;
    setStep("details");
  };

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!newDetails.name.trim()) return;
    createMachine.mutate(newDetails);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Add machine</Button>
      </DialogTrigger>
      {step === "cert" ? (
        <DialogContent>
          <form onSubmit={handleCertContinue}>
            <DialogHeader>
              <DialogTitle>Add machine</DialogTitle>
              <DialogDescription>
                Paste the TPM-backed device certificate generated by the box.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              <Label htmlFor="new-cert">Device certificate</Label>
              <textarea
                id="new-cert"
                value={newDetails.certificate_pem}
                onChange={(e) =>
                  setNewDetails((prev) => ({ ...prev, certificate_pem: e.target.value }))
                }
                placeholder="-----BEGIN CERTIFICATE-----"
                className="min-h-36 w-full rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button type="submit">Continue</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      ) : (
        <DialogContent className="sm:max-w-lg">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add machine</DialogTitle>
              <DialogDescription>Update inventory and sale details.</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <MachineDetailsForm
                details={newDetails}
                onChange={(patch) => setNewDetails((prev) => ({ ...prev, ...patch }))}
                idPrefix="new"
                hardwareOptions={hardwareOptions}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep("cert")}>
                Back
              </Button>
              <Button type="submit" disabled={createMachine.isPending}>
                Add machine
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );
}

function DeleteMachineDialog({ machine }: { machine: Machine }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"closed" | "warn" | "confirm">("closed");
  const [confirmText, setConfirmText] = useState("");

  const deleteMachine = useMutation({
    mutationFn: () => api.deleteMachine(machine.id),
    onSuccess: () => {
      setStep("closed");
      setConfirmText("");
      void queryClient.invalidateQueries({ queryKey: ["machines"] });
    },
  });

  return (
    <>
      <Button
        variant="link"
        className="h-auto p-0 text-destructive"
        onClick={() => setStep("warn")}
      >
        Delete
      </Button>

      <AlertDialog
        open={step === "warn"}
        onOpenChange={(open) => {
          // AlertDialogAction below always closes this dialog as part of its
          // default behavior, firing onOpenChange(false) right after our own
          // onClick has already advanced to "confirm" — only reset to
          // "closed" if we're still on this step (i.e. Cancel was clicked).
          if (!open) setStep((current) => (current === "warn" ? "closed" : current));
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{machine.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the machine and its certificate — it will no
              longer be able to authenticate or fetch updates. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setStep("confirm")}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={step === "confirm"}
        onOpenChange={(open) => {
          if (!open) {
            setStep("closed");
            setConfirmText("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Type <span className="font-mono font-medium text-foreground">{machine.name}</span> to
              confirm permanent deletion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={machine.name}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== machine.name || deleteMachine.isPending}
              onClick={() => deleteMachine.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function Machines() {
  const { data: machines, isLoading } = useQuery({
    queryKey: ["machines"],
    queryFn: api.listMachines,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Machines</h1>
          <p className="text-sm text-muted-foreground">
            Provision machines and assign each one to a release profile.
          </p>
        </div>

        <AddMachineDialog />
      </div>

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Serial #</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Sold</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Last seen</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {machines?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No machines yet.
                  </TableCell>
                </TableRow>
              )}
              {machines?.map((machine) => (
                <TableRow key={machine.id}>
                  <TableCell className="font-medium">
                    {machine.name}
                    {machine.box_number && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        box #{machine.box_number}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {machine.serial_number ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {machine.buyer_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {machine.sold_at ? new Date(machine.sold_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell>
                    <ProfileSelect machineId={machine.id} profileId={machine.profile_id} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(machine.last_seen_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-3">
                      <EditMachineDialog machine={machine} />
                      <Button variant="link" className="h-auto p-0" asChild>
                        <Link to={`/audit?machine=${machine.id}`}>Audit log</Link>
                      </Button>
                      <DeleteMachineDialog machine={machine} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
